import { updateProfile } from 'firebase/auth';
import { query, serverTimestamp, where } from 'firebase/firestore';
import { getAuthColor } from 'global/auth/authPermissions';
import { _appUserPresenceCollection, _appUserPresenceDoc, _currentAppUserCountsCollection, _currentUserIdProp } from 'global/cnr/reducers/AppUserReducer';
import { signInResponseTypes } from 'global/cnr/reducers/SignInReducer';
import { createInitials } from 'global/common/dataAdjust';
import { gEnums } from 'global/enums/globalEnums';
import { createRefPath, createRefPath_client, createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import { doc_get_promise, ref_get } from 'global/firestoreData/appData/fsRefs';
import { getAdditionalAppUser } from 'global/firestoreData/profiles/getAdditionalAppUser';
import { getAdditionalAppUserInfoPromise } from 'global/firestoreData/profiles/getAppUserAdditional';
import { _appUserProfileCollectionName } from 'global/firestoreData/profiles/getProfile';
import { validateEmail } from 'global/validation/validation';
import { _developerAdminEmail } from 'project/appConfiguration';
import { useCallback, useContext } from 'react';
import { AppContext } from '../AppProvider';
import { convertSnapshot } from '../contextHelpers';
import { DatabaseContext } from '../DatabaseContext';
import { PapsContext } from '../PapsContext';
import { useAppUserAccess } from './useAppUserAccess';
import { v4 as uuidv4 } from 'uuid';
import { currentHelpers } from 'global/redirection/current';
import { increment as realtimeIncrement, ref, update } from 'firebase/database';
import { increment as firestoreIncrement } from 'firebase/firestore'
import { getISOWeek } from 'date-fns';
import { PageContext } from '../PageContext';
import _ from 'lodash';

export const useProfile = () => {

  const appUserAccess = useAppUserAccess()
  const { appUserSession } = appUserAccess ?? {}

  const appContext = useContext(AppContext)
  const { app_state } = appContext ?? {}
  const { mainDb, mainStorage, mainAuth, mainRt } = app_state ?? {}

  const pageContext = useContext(PageContext)
  const { currentSettings_state } = pageContext ?? {}
  const { aps_global, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appUserCollection } = aps_appUserSettings ?? {}

  const databaseContext = useContext(DatabaseContext)
  const { database_fns } = databaseContext ?? {}

  const saveCurrentAppUserId = useCallback(({ pathViews }) => {
    const newId = uuidv4();
    currentHelpers.storageItem_set(_currentUserIdProp, newId);
    const today = new Date();
    const year = today.getFullYear();
    const weekNumber = getISOWeek(today)
    const refPathI = createRefPath_event(pathViews, [_currentAppUserCountsCollection, year])
    const dataToUpdateI = { [`week${weekNumber}`]: firestoreIncrement(1) }
    // database_fns.update_doc(refPathI, dataToUpdateI)
    database_fns.update_docc({
      refPath: refPathI,
      data: dataToUpdateI
    })
  }, []);

  const saveDailyUserCount = useCallback(({ pathViews }) => {

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    const todayKey = `${year}-${month}-${day}`;
    const lastTracked = currentHelpers.storageItem_get('_lastDailyUserCount');

    if (lastTracked === todayKey) return;

    const path = `clients/${pathViews.clients}/events/${pathViews.events}/dailyUserCount/${year}/${month}/${day}`

    update(ref(mainRt, path), {
      count: realtimeIncrement(1)
    });

    currentHelpers.storageItem_set('_lastDailyUserCount', todayKey);

  }, []);

  const updateAppProfile = useCallback(({
    pathViews,
    profileData,
    callback
  }) => {
    updateAppProfileDirect({
      mainDb,
      mainAuth,
      pathViews,
      appUserSession,
      profileData,
      callback
    })
  }, []);

  const getProfileInfo = useCallback(({ currentAuthUser, pathViews, aps_appUserSettings, handleAmmendAppUser, handleUpdateAppUserEventInfo, handleAppUserInfo }) => {

    getAppUserInfo(
      {
        mainDb,
        mainStorage,
        mainAuth,
        database_fns,
        currentAuthUser,
        aps_appUserSettings,
        pathViews,
        handleAmmendAppUser,
        handleUpdateAppUserEventInfo,
        handleAppUserInfo
      }

    )
  }, []);

  const updatePresenceStatus = useCallback(({ pathViews, currentAuthUser, presenceStatus }) => {
    if (currentAuthUser && currentAuthUser.uid) {
      const _ref = createRefPath_event(pathViews, [_appUserPresenceCollection, _appUserPresenceDoc])
      const _dataToUpdate = {
        [currentAuthUser.uid]: {
          displayName: currentAuthUser.displayName,
          status: presenceStatus,
          email: currentAuthUser.email,
          timestamp: serverTimestamp(),
        }
      }
      database_fns.set_doc(_ref, _dataToUpdate, true)
    }
  }, []);

  const createClientProfileUrls = useCallback(({ pathViews, appDataDocuments, callback }) => {
    create_clientProfileUrls(database_fns, aps_global, appDataDocuments, callback, mainStorage, pathViews)
  }, []);

  return { createClientProfileUrls, updatePresenceStatus, getProfileInfo, saveCurrentAppUserId, updateAppProfile, saveDailyUserCount }
}

/**
 * Gets the appUser's info from
 * @param {*} param0 
 */
const getAppUserInfo = (
  {
    mainDb,
    mainStorage,
    mainAuth,
    database_fns,
    currentAuthUser,
    aps_appUserSettings,
    pathViews,
    handleAmmendAppUser,
    handleUpdateAppUserEventInfo,
    handleAppUserInfo
  }
) => {


  // get the uid, email, phoneNumber from the currentAuthUser
  const { uid, email, phoneNumber } = currentAuthUser ?? {}
  const creds = { email: email, uid: uid, phoneNumber: phoneNumber }

  // 2nd callback from callback_2
  const callback_3 = async (appUser, appUserAccess) => {
    try {
      const { profileData } = appUser ?? {}
      const { _itemKey } = profileData ?? {}
      getAppUserEventInfo(database_fns, pathViews, aps_appUserSettings, _itemKey, null, handleUpdateAppUserEventInfo)
      handleAmmendAppUser(appUser, appUserAccess)
    } catch (error) {
      console.error("Error in callback_3:", error);
    }
  }

  // 2nd callback from callback_1
  const callback_2 = async (appUser, appUserAccess) => {
    try {
      await getAdditionalAppUser(mainDb, appUser, appUserAccess, pathViews, callback_3);
    } catch (error) {
      console.error("Error in callback_2:", error);
    }
  };

  // first callback from getAppUserProfileInfos
  const callback_1 = async (data) => {
    try {
      const res = await getAdditionalAppUserInfoPromise(mainStorage, mainAuth);
      const { appUser: _appUser, appUserAccess } = ammendAppUserInfo(res, data, currentAuthUser, aps_appUserSettings, pathViews);
      await callback_2(_appUser, appUserAccess);
    } catch (error) {
      console.error("Error in callback_1:", error);
      const { appUser: _appUserD, appUserAccess } = ammendAppUserData(currentAuthUser, data, null, null, aps_appUserSettings, pathViews);
      _appUserD.profileValidated = true;
      _appUserD.loggedIn = true;
      await callback_2(_appUserD, appUserAccess);
    }
  };

  if (currentAuthUser) {
    const callData = { creds, pathViews, aps_appUserSettings }
    getAppUserProfileInfos(mainDb, database_fns, callData, callback_1, handleAppUserInfo)
  } else {
    callback_3({ profileValidated: true }, {})
  }
}

const getAppUserEventInfo = (database_fns, pathViews, aps_appUserSettings, _itemKey, forClone, callback) => {
  const { appUserCollection } = aps_appUserSettings ?? {}
  const _appUserRef = createRefPath_event(pathViews, [appUserCollection ? appUserCollection : 'attendees', _itemKey ? _itemKey : 'test'])
  database_fns.get_data({ refPath: _appUserRef, callback, cbProps: { forClone }, opts: { returnFirstObject: true, listen: true } })
}

export const ammendAppUserData = (currentAuthUser, appUserInfo, claims, imageUrls, aps_appUserSettings, pathViews) => {

  const { email, phoneNumber, displayName, providerIds, providerData } = currentAuthUser ?? {}

  const { pageFavs, settingsFavs, events: events_profile, clients: clients_profile, appUserSessionKey, fcmTokens, id: profileId, activeEvents_appUser, sessionAppUser, appUserViewTypeValues } = appUserInfo ? appUserInfo : { profileLevel: 0 }
  const { authLevel, clientAuthLevel, clients, events } = claims ?? {}
  const { events: eventKey, clients: clientKey } = pathViews ?? {}

  let appUserAccessType = gEnums.appUserAccessTypes.appProfile
  let accessLevel;
  let settingsAuthLevel;
  const accessLevelTypes = [];

  // Set the Access level here

  if (events_profile && eventKey && events_profile[eventKey]) {
    appUserAccessType = gEnums.appUserAccessTypes.appProfile
    accessLevel = gEnums.accessLevels.appProfile.value
  } else if (clients_profile && clientKey && clients_profile[clientKey] && clients_profile[clientKey].events && clients_profile[clientKey].events[eventKey]) {
    appUserAccessType = gEnums.appUserAccessTypes.appProfile
    accessLevel = gEnums.accessLevels.appProfile.value
  }

  if (clientAuthLevel) {
    settingsAuthLevel = clientAuthLevel
    accessLevel = clientAuthLevel
    appUserAccessType = gEnums.appUserAccessTypes.appAdmin
  }

  if (authLevel) {
    settingsAuthLevel = authLevel
    accessLevel = authLevel
    if (authLevel >= gEnums.accessLevels.admin.value) {
      appUserAccessType = gEnums.appUserAccessTypes.admin
    }
  }

  if (email === _developerAdminEmail) {
    accessLevel = gEnums.accessLevels.superAdminDeveloper.value
    settingsAuthLevel = gEnums.accessLevels.superAdminDeveloper.value
    appUserAccessType = gEnums.appUserAccessTypes.admin
  }

  if (sessionAppUser) {
    const { appUserEvent } = sessionAppUser ?? {}
    const { appUserDataModifications, appUserViewTypeProp } = aps_appUserSettings
    if (appUserDataModifications && appUserEvent && appUserEvent[appUserViewTypeProp]) {
      const _appUserEventViewTypes = appUserEvent[appUserViewTypeProp]
      _.forEach(_appUserEventViewTypes, (vt) => {
        if (appUserDataModifications[vt]) {
          const audms = appUserDataModifications[vt]
          appUserAccessType = gEnums.appUserAccessTypes.appDataAdminLimited
          accessLevel = gEnums.accessLevels.appDataAdminLimited.value
          _.forEach(audms, (dm) => {
            switch (dm) {
              case 'all':
                appUserAccessType = gEnums.appUserAccessTypes.appDataAdmin
                accessLevel = gEnums.accessLevels.appDataAdmin.value
                break;
              default:
                accessLevelTypes.push(dm)
            }
          })
        }
      })
    }
  }

  const tokens = { accessLevel, clients, events }

  let initials = displayName ? createInitials(displayName) : null
  if (!initials && appUserInfo && appUserInfo.displayName) { initials = createInitials(appUserInfo.displayName) }

  let isAppUser;
  let address;

  switch (appUserAccessType) {
    case gEnums.appUserAccessTypes.appProfile:
    case gEnums.appUserAccessTypes.appAdmin:
    case gEnums.appUserAccessTypes.appDataAdmin:
    case gEnums.appUserAccessTypes.appDataAdminLimited:
    case gEnums.appUserAccessTypes.appSubAdmin:
      isAppUser = true
      break;
    default:
    // nothing
  }

  if (sessionAppUser) {
    const { street, city, state, zip } = sessionAppUser ?? {}
    address = street + ', ' + city + ', ' + state + ' ' + zip
  }

  // const { eventFavs, eventFavKeys } = getEventFavs(pageFavs, pathViews.events)

  // const favs_info = {
  //   eventFavs,
  //   eventFavKeys,
  // }

  // if (appUserInfo) {
  //   appUserInfo.favs_info = favs_info
  // }

  const hasGoogleAccount = _.includes(providerIds, 'google.com')
  const googleProvider = hasGoogleAccount ? _.find(providerData, { providerId: 'google.com' }) : null

  const appUserAccess = {
    accessLevel,
    accessLevelTypes,
    activeEvents_appUser,
    address,
    appUserAccessType,
    appUserColor: getAuthColor(accessLevel),
    appUserSession: sessionAppUser,
    appUserSessionKey,
    appUserViewTypeValues,
    displayName: appUserInfo && appUserInfo.displayName,
    email,
    // favs_info,
    fcmTokens,
    initials,
    isAdminOrSuper: settingsAuthLevel >= gEnums.accessLevels.admin.value,
    isAppUser,
    isDataAdmin: settingsAuthLevel >= gEnums.authLevels.appDataAdmin.value,
    isSuperAdmin: settingsAuthLevel >= gEnums.accessLevels.superAdmin.value,
    isSuperAdminDeveloper: settingsAuthLevel >= gEnums.accessLevels.superAdminDeveloper.value,
    loggedIn: true,
    phoneNumber,
    profileId,
    settingsAuthLevel,
    uid: currentAuthUser.uid,
    hasGoogleAccount: hasGoogleAccount,
    googleEmail: googleProvider?.email,
  }

  // ammend the appUserAccess
  if (events_profile) { appUserAccess.events = events_profile }

  if (clients_profile) {
    appUserAccess.clients = clients_profile
    appUserAccess.events = clients_profile[clientKey] ? clients_profile[clientKey].events : null
  }

  if (clientAuthLevel) { appUserAccess.appAuth = { clientAuthLevel, clientAuthKey: clientAuthLevel ? clientKey : null } }

  switch (appUserAccessType) {
    case gEnums.appUserAccessTypes.appAdmin:
    case gEnums.appUserAccessTypes.appDataAdmin:
    case gEnums.appUserAccessTypes.appDataAdminLimited:
    case gEnums.appUserAccessTypes.appProfile:
    case gEnums.appUserAccessTypes.appSubAdmin:
      appUserAccess.clientAccessKey = clientKey ? clientKey : null
      break;
    default:
    // nothing
  }


  const appUser = {
    ...currentAuthUser,
    appUserAccess,
    imageUrls: imageUrls,
    profileData: appUserInfo,
    settingsFavs,
    tokens,
  }

  return { appUser, appUserAccess }

}

export const getAppUserProfileInfos = async (mainDb, database_fns, data, callback, callback_appUser) => {

  const { creds, pathViews, aps_appUserSettings, eventAppUsers, noEmailValidation, showLogs } = data
  const { appUserCollection } = aps_appUserSettings ?? {}
  const { clients: clientKey, events: eventKey } = pathViews ?? {}
  const { email, userName, uid, phoneNumber } = creds ?? {}

  // userName/email
  let _userNameOrEmail = userName ? userName : email
  if (_userNameOrEmail) { _userNameOrEmail = _userNameOrEmail.toLowerCase().trim() }

  // validate email
  if (!noEmailValidation && !validateEmail(_userNameOrEmail)) {
    if (!uid) {
      callback(null, null, { error: signInResponseTypes.invalidEmail })
    }
  }

  const callback_profileData = (appUserInfo) => {
    if (_.isEmpty(appUserInfo) || !appUserInfo.isValidAppUser) {
      callback(null, null, { error: signInResponseTypes.profileNotFound })
    } else {
      callback(appUserInfo)
    }
  }

  /**
   * profileInfo contains data from the profile collection
   * @param {*} profileInfo 
   */
  const callback_profile = async (profileInfo) => {

    // if it doesn't have a profile at the root level, it may have one at the client level  
    const _allRefs = allRefs(mainDb, pathViews, clientKey, eventKey, uid, appUserCollection, _userNameOrEmail, phoneNumber)

    let data_client;

    if (clientKey) {
      data_client = await database_fns.get_docs(_allRefs.data_client, { returnFirstObject: true })
    }

    if (eventKey && _allRefs.data_event) {
      const data_event = await database_fns.get_docs(_allRefs.data_event, { returnFirstObject: true })
      const { appUserCollection: appUserView_event } = data_event ?? {}
      const _props = { pathViews, userNameOrEmail: _userNameOrEmail, refs: _allRefs, aps_appUserSettings, appUserCollection: appUserView_event ? appUserView_event : appUserCollection, data_client, data_event, eventAppUsers, profileInfo, eventKey, showLogs }
      const appUserInfo = await getAppUserProfileData(_props)
      callback_profileData(appUserInfo)
    } else {
      const _props2 = { pathViews, userNameOrEmail: _userNameOrEmail, refs: _allRefs, aps_appUserSettings, appUserCollection: appUserCollection, data_client, profileInfo, eventKey, showLogs }
      const appUserInfo = await getAppUserProfileData(_props2)
      callback_profileData(appUserInfo)
    }
  }

  try {
    // gets the data from the PROFILE collection at the ROOT of the database
    getData_fromProfiles(database_fns, _userNameOrEmail, uid, callback_profile)
    callback_appUser && getData_fromAppUsers(database_fns, _userNameOrEmail, uid, callback_appUser)
  } catch (error) {
    console.log('e', error)
  }

}

const getAppUserProfileData = async (props) => {
  const { pathViews, userNameOrEmail, refs, aps_appUserSettings, profileInfo, eventKey, data_client, data_event, eventAppUsers, callback } = props
  try {
    const promiseResults = await appUserProfile_promises(refs);
    const pd = getAppUserPromiseData(promiseResults, profileInfo, eventAppUsers, userNameOrEmail, pathViews);
    const appUser = ammendAppUser(aps_appUserSettings, pd, eventKey, data_client, data_event);
    if (appUser) {
      callback && callback(appUser);
      return appUser
    } else {
      callback && callback({});
      return {}
    }
  } catch (error) {
    console.error(error);
    callback && callback({});
    return {}
  }
}

const appUserProfile_promises = (refs) => {
  const promises = []
  if (refs.profile_appUser) { promises.push(doc_get_promise(refs.profile_appUser)) } // 1 - profile_appUser 
  if (refs.profile_client) { promises.push(doc_get_promise(refs.profile_client)) } // 2 - profile_client 
  if (refs.profile_event) { promises.push(doc_get_promise(refs.profile_event)) } // 3 - profile_event  
  return Promise.all(promises)
}

const getAppUserPromiseData = (promiseResult, profileInfo, eventAppUsers, userNameOrEmail) => {

  const pras = {
    profile_appUser: 0,
    profile_client: 1,
    profile_event: 2,
  }

  const pd = {
    profile: profileInfo, //profiles 
    profile_appUser: promiseResult[pras.profile_appUser] ? convertSnapshot(promiseResult[pras.profile_appUser], true, { returnFirstObject: true }) : null, //appUserProfiles
    profile_client: promiseResult[pras.profile_client] ? convertSnapshot(promiseResult[pras.profile_client], true, { returnFirstObject: true }) : null,// clients/profiles
    profile_event: promiseResult[pras.profile_event] ? convertSnapshot(promiseResult[pras.profile_event], true, { returnFirstObject: true }) : null,  // clients/events/attendees
  }

  if (eventAppUsers && userNameOrEmail) {
    if (!pd.profile_event) {
      const eventAppUser = _.find(eventAppUsers, { email: userNameOrEmail })
      const { firstName, lastName } = eventAppUser ?? {}
      const displayName = firstName + ' ' + lastName
      pd.profile_event = _.cloneDeep(eventAppUser)
      pd.profile_event.displayName = displayName
      if (!pd.profile_client) {
        pd.profile_client = _.cloneDeep(eventAppUser)
        pd.profile_client.displayName = displayName
      }
    }
  }

  return pd

}

const ammendAppUser = (aps_appUserSettings, pd, eventKey, data_client) => {

  let au = {};

  const profileSources = []

  const { appUserCollection } = aps_appUserSettings ?? {}
  const { profile_appUser, profile_event, profile_client, profile } = pd

  const { events: events_cp } = profile_client ?? {}
  const { activeEvents: activeEvents_client } = data_client ?? {}

  const currentEvent = events_cp ? events_cp[eventKey] : null
  const { appUserKey: appUserKey_event } = currentEvent ?? {}

  const appUserEventKeys = events_cp ? Object.keys(events_cp) : []

  const activeEvents_appUser = []

  if (activeEvents_client) {
    appUserEventKeys.forEach(auek => {
      if (activeEvents_client.includes(auek)) {
        activeEvents_appUser.push(auek)
      }
    })
  }

  if (profile_appUser) {
    // attendees
    au = profile_appUser
    au.appUserAccessType = gEnums.appUserAccessTypes.appUser
    au.profileOriginType = gEnums.profileTypes.clientProfiles
    profileSources.push(appUserCollection ? appUserCollection : 'attendees')
  }

  // CLIENT PROFILE
  if (profile_client) {
    au = profile_client
    au.appUserAccessType = gEnums.appUserAccessTypes.appProfile
    au.profileOriginType = gEnums.profileTypes.clientProfiles
    profileSources.push('clients')
  }

  // ADMIN - //profiles
  if (profile) {
    au = profile
    au.appUserAccessType = gEnums.appUserAccessTypes.admin
    au.profileOriginType = gEnums.profileTypes.adminProfiles
    profileSources.push('profiles')
  }

  if (au) {

    au.profileSources = profileSources

    if (profile_event && profile_event.id) { au.appUserSessionKey = profile_event.id }
    if (profile_appUser && profile_appUser.key) { au.appUserSessionKey = profile_appUser.key }
    if (profile_appUser && profile_appUser.id) { au.appUserSessionKey = profile_appUser.id }

    if (appUserKey_event) { au.appUserSessionKey = appUserKey_event }
    if (profile_appUser) { au.sessionAppUser = _.cloneDeep(profile_appUser) }
    if (profile_event) { au.appUserEvent = _.cloneDeep(profile_event) }
    if (profile_client) { au.sessionAppUser = _.cloneDeep(profile_client) }
    if (activeEvents_client) { au.activeEvents_client = _.cloneDeep(activeEvents_client) }
    if (activeEvents_appUser) { au.activeEvents_appUser = _.cloneDeep(activeEvents_appUser) }

    if (au.appUserEvent) {
      const { appUserViewTypeProp } = aps_appUserSettings
      if (au.appUserEvent && au.appUserEvent[appUserViewTypeProp]) {
        au.appUserViewTypeValues = au.appUserEvent[appUserViewTypeProp]
      }
    }
  }

  if (profile || profile_event || profile_appUser) {
    au.isValidAppUser = true
  }

  return au
}

const create_clientProfileUrls = async (database_fns, aps_global, appUserCollection, appDataDocuments, callback, mainStorage, pathViews) => {

  const cb = async (a) => {
    const { galleryItems } = a
    if (database_fns) {
      const reff = createRefPath_client(pathViews, ['profileGallery', 'galleryItems'])
      const xxx = {}
      _.forEach(galleryItems, (item, itemKey) => {
        const { urls, customMetadata } = item
        xxx[itemKey] = {
          urls,
          customMetadata
        }
      })
      console.log('reff', reff, xxx)
      await database_fns.set_doc(reff, xxx, false)
      callback('done')
    }
  }
  const { storageOptions } = aps_global ?? {}
  const { clientStorageImageCollections } = storageOptions ?? {}
  let allEmails = []
  _.forEach(clientStorageImageCollections, (imageCollection) => {
    const appDataDocument = appDataDocuments?.[imageCollection]
    const emails = appDataDocument ? _.map(_.filter(appDataDocument, 'email'), 'email') : [];
    allEmails = _.concat(allEmails, emails)
  })
  getGallery_clientProfiles(mainStorage, pathViews, appUserCollection, null, cb, gEnums.storageLocationTypes.clientProfiles, allEmails)
}

/**
 * gets the data from the PROFILE collection at the ROOT of the database
 * @param {*} database_fns 
 * @param {*} email 
 * @param {*} uid 
 * @param {*} callback 
 */
const getData_fromProfiles = async (database_fns, email, uid, callback) => {
  if (email) {
    const wheres = [where('email', '==', email)]
    const _refPath = createRefPath(['profiles'])
    database_fns.get_data({ refPath: _refPath, wheres, callback, opts: { listen: true, returnFirstObject: true } })
  } else if (uid) {
    const _refPath = createRefPath(['profiles', uid])
    database_fns.get_data({ refPath: _refPath, callback, opts: { listen: true, returnFirstObject: true } })
  }
}

const getData_fromAppUsers = async (database_fns, email, uid, callback) => {
  if (email) {
    const wheres = [where('email', '==', email)]
    const _refPath = createRefPath(['appUsers'])
    database_fns.get_data({ refPath: _refPath, wheres, callback, opts: { listen: true, returnFirstObject: true } })
  } else if (uid) {
    const _refPath = createRefPath(['appUsers', uid])
    database_fns.get_data({ refPath: _refPath, callback, opts: { listen: true, returnFirstObject: true } })
  }
}

const allRefs = (mainDb, pathViews, clientKey, eventKey, uid, appUserCollection, _userNameOrEmail, phoneNumber) => {
  const refs = {
    profile: null,
    profile_appUser: null,
    profile_client: null,
    data_client: null,
    data_event: null,
    profile_event: null,
  }

  refs.profile_appUser = _userNameOrEmail || phoneNumber ? appUserProfileRef(mainDb, _userNameOrEmail, phoneNumber) : null
  refs.profile_client = (_userNameOrEmail || phoneNumber) && pathViews.clients ? clientProfileRef(mainDb, pathViews, _userNameOrEmail, uid, phoneNumber) : null
  refs.data_client = clientKey ? clientRef(mainDb, pathViews) : null
  refs.data_event = eventKey ? eventRef(mainDb, pathViews) : null
  refs.profile_event = (_userNameOrEmail || phoneNumber) && pathViews.events ? eventProfileRef(mainDb, pathViews, _userNameOrEmail, uid, phoneNumber, appUserCollection) : null
  return refs
}

const appUserProfileRef = (mainDb, email, phoneNumber) => {
  try {
    if (email) {
      const wheres = [where('email', '==', email)]
      return ref_get(mainDb, createRefPath([_appUserProfileCollectionName]), wheres)
    } else if (phoneNumber) {
      const wheres = [where('phoneNumber', '==', phoneNumber)]
      return ref_get(mainDb, createRefPath([_appUserProfileCollectionName]), wheres)
    }
  } catch (error) {
    console.log('appUserProfileRef', error)
    return null
  }

}

const clientProfileRef = (mainDb, pathViews, email, uid, phoneNumber) => {
  try {
    const _refPath = createRefPath_client(pathViews, ['profiles'])
    if (email) {
      const wheres = [where('email', '==', email)]
      return ref_get(mainDb, createRefPath_client(pathViews, ['profiles']), wheres)
    } else if (phoneNumber) {
      const wheres = [where('phoneNumber', '==', phoneNumber)]
      return ref_get(mainDb, createRefPath_client(pathViews, ['profiles']), wheres)
    } else if (uid) {
      return ref_get(mainDb, createRefPath([uid], _refPath))
    }
  } catch (error) {
    console.log('clientProfileRef', error)
    return null
  }

}

const clientRef = (mainDb, pathViews) => {
  try {
    return ref_get(mainDb, createRefPath_client(pathViews))
  } catch (error) {
    console.log('clientRef', error)
    return null
  }
}

/**
 * 
 * @param {*} mainDb 
 * @param {*} pathViews 
 * @returns a ref from `events`
 */
const eventRef = (mainDb, pathViews) => {
  try {
    ref_get(mainDb, createRefPath_event(pathViews))
  } catch (error) {
    console.log('eventRef', error)
    return null
  }
}

const eventProfileRef = (mainDb, pathViews, email, uid, phoneNumber, appUserCollection) => {
  try {
    if (appUserCollection) {
      if (email) {
        const wheres = [where('email', '==', email)]
        return ref_get(mainDb, createRefPath_event(pathViews, [appUserCollection]), wheres)
      } else if (phoneNumber) {
        const wheres = [where('phoneNumber', '==', phoneNumber)]
        return ref_get(mainDb, createRefPath_event(pathViews, [appUserCollection]), wheres)
      } else if (uid) {
        return ref_get(mainDb, createRefPath_event(pathViews), [appUserCollection, uid])
      }
    }
  } catch (error) {
    console.log('eventProfileRef', error)
    return null
  }
}

const ammendAppUserInfo = (promiseResults, appUserInfo, currentAuthUser, aps_appUserSettings, pathViews) => {
  const { claims } = promiseResults[0]
  const imageUrls = {
    full: promiseResults && promiseResults[1] && promiseResults[1][0] ? promiseResults[1][0] : null,
    thumbnail: promiseResults && promiseResults[1] && promiseResults[1][1] ? promiseResults[1][1] : null
  }

  const { appUser, appUserAccess } = ammendAppUserData(currentAuthUser, appUserInfo, claims, imageUrls, aps_appUserSettings, pathViews)

  appUser.profileValidated = true
  appUser.loggedIn = true

  return { appUser, appUserAccess }
}

const updateAppProfileDirect = async ({ mainDb, mainAuth, appUserSession, pathViews, profileData, callback }) => {

  let updateData = {}
  const { appUserAccessType, email } = appUserSession ?? {}

  let _profileType;

  switch (appUserAccessType) {
    case gEnums.appUserAccessTypes.admin:
    case gEnums.appUserAccessTypes.superAdmin:
    case gEnums.appUserAccessTypes.superAdminDeveloper:
      _profileType = gEnums.profileTypes.adminProfiles
      break;
    default:
      _profileType = gEnums.profileTypes.clientProfiles
      break
  }

  switch (_profileType) {
    case gEnums.profileTypes.adminProfiles:
      updateData = {
        displayName: profileData.displayName,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        initials: profileData.initials,
      }
      const updateProps_admin = ['firstName', 'lastName', 'initials', 'email', 'phoneNumber', 'street', 'city', 'state', 'zip', 'displayName', 'aboutMe', 'bio', 'locationSettings', 'notificationSettings', 'permissionSettings']
      ammendUpdateData(updateProps_admin, profileData, updateData)
      if (profileData.aboutMe) { updateData.aboutMe = profileData.aboutMe }
      if (profileData.bio) { updateData.bio = profileData.bio }
      if (profileData.notificationSettings) { updateData.notificationSettings = profileData.notificationSettings }
      if (profileData.permissionSettings) { updateData.permissionSettings = profileData.permissionSettings }
      if (profileData.profileUpdated) { updateData.profileUpdated = profileData.profileUpdated }
      break;

    default:
      updateData = {}
      const updateProps = ['firstName', 'lastName', 'initials', 'email', 'phoneNumber', 'street', 'city', 'state', 'zip', 'displayName',
        'aboutMe', 'bio',
        'hideAboutMeFromApp', 'hideAddressFromApp', 'hideBioFromApp', 'hideEmailFromApp', ' hidePhoneNumberFromApp',
        'locationSettings', 'notificationSettings', 'permissionSettings', 'disclaimerResponses',
        'appUserPrivacy'
      ]
      ammendUpdateData(updateProps, profileData, updateData)
      if (!profileData.displayName && updateData.firstName && updateData.lastName) { updateData.displayName = updateData.firstName + ' ' + updateData.lastName }
      if (!updateData.initials && updateData.displayName) { updateData.initials = createInitials(updateData.displayName) }
      if (profileData.profileUpdated) { updateData.profileUpdated = profileData.profileUpdated }
      break;
  }

  let _baseRef;

  switch (_profileType) {
    case gEnums.profileTypes.adminProfiles:
      _baseRef = createRefPath(['profiles'])
      break;
    default:
      _baseRef = createRefPath_client(pathViews, ['profiles'])
  }

  const cr = collection(mainDb, _baseRef)
  const q = query(cr, where("email", "==", email));

  getDocs(q).then(data => {
    if (!data.empty) {
      const itemData = convertSnapshot(data, false, { returnFirstObject: true })
      const { _itemKey } = itemData
      const _setRef = createRefPath([_itemKey], _baseRef)
      if (_allowProfileUpdate) {
        updateAuthProfile(profileData)
        database_fns.set_doc(_setRef, updateData, true, callback)
      } else {
        console.log('_setRef', _setRef, updateData)
      }
    } else {
      const _addRef = createRefPath([email], _baseRef)
      updateData.email = email
      if (_allowProfileUpdate) {
        updateAuthProfile(mainAuth, profileData)
        database_fns.set_doc(_addRef, updateData, callback)
      } else {
        console.log('_addRef', _addRef, updateData)
      }
    }
  }).catch(error => {
    callback({ type: dispatchProps.error, error })
  })
}

const updateAuthProfile = (mainAuth, profileData) => {
  const currentUser = mainAuth.currentUser
  if (!currentUser.displayName) {
    // update profile if display name is missing  
    updateProfile(mainAuth.currentUser, {
      displayName: profileData.firstName + ' ' + profileData.lastName
    }).then(() => {
      // return { success: true, signInResponseType: signInResponseTypes.signInSuccess }
      // ...
    }).catch((error) => {
      // return { success: true, signInResponseType: signInResponseTypes.signInSuccess }
      // ...
    });
  } else {
    // resp = { success: true, signInResponseType: signInResponseTypes.signInSuccess }
  }
}

const ammendUpdateData = (updateProps, profileData, updateData) => {
  updateProps.forEach(prop => {
    if (profileData[prop]) { updateData[prop] = profileData[prop] }
  })
}